import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';

/**
 * The words the spoken brief is reading, sentence by sentence — what the listen-along modal highlights.
 *
 * Fetched rather than derived from the brief the home already has. The spoken form is NOT the summary as
 * the card renders it: markdown is stripped, links are rewritten, an over-long brief is truncated at a
 * sentence. Re-deriving that here (and again in Swift, and again in Kotlin) would put three sanitizers
 * in the way of one requirement — that the highlight moves through exactly the text being read.
 */
export interface BriefScript {
  /** The whole spoken text. The reading direction is decided from this, never from one segment. */
  text: string;
  /** The sentences, in order. Rendered joined by a space — speech has no paragraphs. */
  segments: string[];
}

export async function fetchBriefScript(): Promise<BriefScript> {
  const res = await apiFetch(`${API_BASE}/api/voice/brief-script`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as Partial<BriefScript>;
  return { text: data.text ?? '', segments: data.segments ?? [] };
}

/**
 * `enabled` so the script is fetched when the user asks to listen, not on every home load.
 *
 * Keyed on the BRIEF, not global. A cached script outliving the brief it belongs to would leave the
 * marker moving through yesterday's sentences while today's audio plays — a session left open across the
 * daily brief is all it takes.
 */
export function useBriefScript(conversationId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.briefScript(conversationId),
    queryFn: fetchBriefScript,
    enabled: enabled && !!conversationId,
  });
}
